'use client'

import { useState, useTransition } from 'react'
import { Star } from 'lucide-react'
import { submitReview } from '../_actions/review-actions'

interface ReviewFormProps {
  companyId: string
  onSubmitted?: () => void
}

function StarRating({ value, onChange, label }: { value: number; onChange: (v: number) => void; label: string }) {
  return (
    <div className="flex items-center gap-2">
      <span
        className="w-32 text-sm text-[var(--color-text-secondary)]"
        style={{ fontFamily: 'var(--font-inter, Inter, sans-serif)' }}
      >
        {label}
      </span>
      <div className="flex gap-0.5">
        {[1, 2, 3, 4, 5].map((star) => (
          <button
            key={star}
            type="button"
            onClick={() => onChange(star)}
            className="rounded p-0.5 transition-colors hover:bg-[var(--color-bg-elevated)]"
            aria-label={`${star} star${star > 1 ? 's' : ''}`}
          >
            <Star
              className="h-5 w-5"
              style={{
                color: star <= value ? 'var(--color-accent)' : 'var(--color-text-muted)',
                fill: star <= value ? 'var(--color-accent)' : 'none',
              }}
            />
          </button>
        ))}
      </div>
    </div>
  )
}

export function ReviewForm({ companyId, onSubmitted }: ReviewFormProps) {
  const font = { fontFamily: 'var(--font-inter, Inter, sans-serif)' }
  const [quality, setQuality] = useState(0)
  const [communication, setCommunication] = useState(0)
  const [deadline, setDeadline] = useState(0)
  const [professionalism, setProfessionalism] = useState(0)
  const [title, setTitle] = useState('')
  const [content, setContent] = useState('')
  const [collaborationType, setCollaborationType] = useState('')
  const [error, setError] = useState('')
  const [isPending, startTransition] = useTransition()

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    setError('')

    if (quality === 0 || communication === 0 || deadline === 0 || professionalism === 0) {
      setError('Please rate all dimensions.')
      return
    }
    if (title.trim().length < 5) {
      setError('Title must be at least 5 characters.')
      return
    }
    if (content.trim().length < 20) {
      setError('Review must be at least 20 characters.')
      return
    }

    startTransition(async () => {
      const result = await submitReview(companyId, {
        quality_rating: quality,
        communication_rating: communication,
        deadline_rating: deadline,
        professionalism_rating: professionalism,
        title: title.trim(),
        content: content.trim(),
        collaboration_type: collaborationType || undefined,
      })
      if (result.success) {
        setTitle('')
        setContent('')
        setQuality(0)
        setCommunication(0)
        setDeadline(0)
        setProfessionalism(0)
        setCollaborationType('')
        onSubmitted?.()
      } else {
        setError(result.error ?? 'Failed to submit review.')
      }
    })
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <h3 className="text-sm font-semibold text-[var(--color-text-primary)]" style={font}>
        Write a Review
      </h3>

      <div className="space-y-2" aria-label="Rating inputs">
        <StarRating value={quality} onChange={setQuality} label="Quality" />
        <StarRating value={communication} onChange={setCommunication} label="Communication" />
        <StarRating value={deadline} onChange={setDeadline} label="Deadlines" />
        <StarRating value={professionalism} onChange={setProfessionalism} label="Professionalism" />
      </div>

      <input
        type="text"
        placeholder="Review title"
        value={title}
        onChange={(e) => setTitle(e.target.value)}
        maxLength={100}
        className="w-full rounded-[var(--radius-md)] border border-[var(--color-border-default)] bg-[var(--color-bg-page)] px-3 py-2 text-sm text-[var(--color-text-primary)] placeholder:text-[var(--color-text-muted)] focus:border-[var(--color-brand-indigo)] focus:outline-none"
        style={font}
      />

      <textarea
        placeholder="Share your experience working with this company..."
        value={content}
        onChange={(e) => setContent(e.target.value)}
        maxLength={2000}
        rows={4}
        className="w-full rounded-[var(--radius-md)] border border-[var(--color-border-default)] bg-[var(--color-bg-page)] px-3 py-2 text-sm text-[var(--color-text-primary)] placeholder:text-[var(--color-text-muted)] focus:border-[var(--color-brand-indigo)] focus:outline-none"
        style={font}
      />

      <select
        value={collaborationType}
        onChange={(e) => setCollaborationType(e.target.value)}
        className="rounded-[var(--radius-md)] border border-[var(--color-border-default)] bg-[var(--color-bg-page)] px-3 py-2 text-sm text-[var(--color-text-primary)] focus:border-[var(--color-brand-indigo)] focus:outline-none"
        style={font}
      >
        <option value="">Collaboration type (optional)</option>
        <option value="job">Job / Employment</option>
        <option value="project">Project-based</option>
        <option value="freelance">Freelance</option>
      </select>

      {error && (
        <p className="text-xs text-[var(--color-error)]" style={font}>{error}</p>
      )}

      <button
        type="submit"
        disabled={isPending}
        className="rounded-[var(--radius-md)] bg-[var(--color-brand-indigo)] px-4 py-2 text-sm font-medium text-white transition-opacity hover:opacity-90 disabled:opacity-50"
        style={font}
      >
        {isPending ? 'Submitting...' : 'Submit Review'}
      </button>
    </form>
  )
}
